import { AUTH_API } from '../../../../../api/amplify';
import { useAppDispatch, useAppSelector, useCountdown, useForm } from '../../../../../hooks';
import { Box, Link, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import { SOMETHING_WENT_WRONG, SUCCESS_SIGNUP } from '../../../../../constants/authMessages';
import { FormButton, FormInput } from '../../../../UI';
import { setCognitoUserAuthorized, setCognitoUserPassword } from '../../../../../store/CognitoSlice';
import { useNavigate } from 'react-router-dom';
import { PATHS } from '../../../../../constants/routes';
import { translateErrorMessage } from '../../../../../utils/authHelpers';
import { showAlert } from '../../../../../store';
import { AlertType } from '../../../../../types/common';

const INIT_INPUT = {
  code: '',
};

const TIME_BEFORE_NEXT_ATTEMPT_SEC = 59;

export const ConfirmEmail = () => {
  const navigate = useNavigate();

  const dispatch = useAppDispatch();

  const {
    cognitoUser: { email, password },
  } = useAppSelector((state) => state.cognito);

  const { input, error, handleChange, submit, handlePressEnter, resetForm } = useForm(INIT_INPUT, handleSubmit);

  const [isLoading, setIsLoading] = useState(false);

  const { count, startCountdown } = useCountdown(TIME_BEFORE_NEXT_ATTEMPT_SEC);

  async function handleSubmit() {
    try {
      if (!email) {
        return;
      }

      setIsLoading(true);

      const { code } = input;
      const confirmResponse = await AUTH_API.confirmSignUp(email, code);

      if (!confirmResponse) {
        throw new Error(SOMETHING_WENT_WRONG);
      }

      dispatch(showAlert({ type: AlertType.Success, message: SUCCESS_SIGNUP }));

      const signinResponse = await AUTH_API.signIn(email, password);
      dispatch(setCognitoUserPassword(''));

      if (signinResponse) {
        dispatch(setCognitoUserAuthorized());
        navigate(PATHS.dashboard);
        resetForm();
      }
    } catch (e: any) {
      const errorMessage = translateErrorMessage(e);
      dispatch(showAlert({ type: AlertType.Error, message: errorMessage }));
    }

    setIsLoading(false);
  }

  const resendConfirmationCode = async () => {
    try {
      await AUTH_API.resendConfirmationCode(email);
    } catch (e: any) {
      const errorMessage = translateErrorMessage(e);
      dispatch(showAlert({ type: AlertType.Error, message: errorMessage }));
    } finally {
      startCountdown();
      setIsLoading(false);
    }
  };

  return (
    <Stack
      sx={{
        width: '100%',
        maxWidth: '400px',
        margin: '0 auto',
        gap: '60px',
      }}
    >
      <Stack onKeyPress={handlePressEnter}>
        <Typography
          variant="h1"
          mb={'30px'}
          align={'center'}
          sx={{
            typography: {
              mobile: 'h3',
              laptop: 'h1',
            },
          }}
        >
          Подтвердите почту
        </Typography>

        <Typography variant="body1" mb={'30px'} align={'center'}>
          Введите код, отправленный на email
        </Typography>

        <Stack gap={'20px'} component={'form'} onSubmit={submit}>
          <Box>
            <FormInput
              onChange={handleChange}
              error={error?.code || ''}
              name={'code'}
              value={input.code || ''}
              placeholder={'Код'}
              label={'Код'}
              smallLabel
            />
          </Box>
          <Stack gap={'20px'}>
            <FormButton submit isLoading={isLoading}>
              Отправить
            </FormButton>

            {count > 0 ? (
              <Typography variant="body1" align="center">
                Повторная отправка возможна через {count}
              </Typography>
            ) : (
              <Link component={'button'} underline={'none'} onClick={resendConfirmationCode} type={'button'}>
                Отправить код еще раз
              </Link>
            )}
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};
